import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Accordion, Menu } from 'semantic-ui-react';

import { useTranslation } from 'react-i18next';

import { addUserParamsToUrl } from '../../Pages/AppStore/utils';
import { useRoutes } from '../navigationHooks';
import { buildMarketplaceLink } from '../navigationUtils';
import { RouteNameEnum, RouteType } from '../types';
import styles from './navigation.module.scss';

import {
  BroadcastIcon,
  CaretDownIcon,
  DocumentIcon,
  FoodProgramIcon,
  HelpIcon,
  HomeIcon,
  ManagementIcon,
  PaymentsIcon,
  ReportsIcon,
  SchoolIcon,
  SettingsIcon,
  SuccessIcon,
  UserGroup3Icon,
  UserIcon,
  WebsiteIcon,
} from '@wonderschool/common-base-ui';

import { LinkOutIcon } from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ReactComponent as AppStoreIcon } from '../../assets/images/svg/apps.svg';
import { getConfig } from '../../config/env';
import { useOrganization } from '../../hooks/useOrganizations';

const ROUTE_TRANSLATION_KEYS: Partial<Record<RouteNameEnum, string>> = {
  [RouteNameEnum.MARKETING]: 'navigation.marketing.marketing',
  [RouteNameEnum.WONDERSCHOOL_PHONE_NUMBER]: 'navigation.marketing.wonderschoolPhoneNumber',
  [RouteNameEnum.BUSINESS_COACHING_SURVEY]: 'navigation.schoolManagement.businessCoachingSurvey',
  [RouteNameEnum.CHECK_IN_CHECK_OUT]: 'navigation.schoolManagement.checkInCheckOut',
  [RouteNameEnum.MEAL_LOGGING]: 'navigation.schoolManagement.mealLogging',
  [RouteNameEnum.BILLING_SIGN_UP]: 'navigation.billing.billingSignUp',
  [RouteNameEnum.TUITION_PRICING_COACHING]: 'navigation.tuitionManagement.tuitionPricingCoaching',
  [RouteNameEnum.ANNUAL_TUITION_INCREASES]: 'navigation.tuitionManagement.annualTuitionIncreases',
  [RouteNameEnum.TAXES]: 'navigation.finance.taxes',
  [RouteNameEnum.SUBSIDY_PREPAYMENT]: 'navigation.billing.subsidyPrepayment',
  [RouteNameEnum.SUBSIDY_PREPAYMENT_NOVA]: 'navigation.billing.fastSubsidy',
  [RouteNameEnum.TRI_SHARE]: 'navigation.governmentSolutions.triShare',
  [RouteNameEnum.WHITE_LABEL_WEBSITE]: 'navigation.listingsAndWebsites.whiteLabelWebsite',
  [RouteNameEnum.YELP]: 'navigation.marketing.yelpManager',
};

const getRouteTranslationKey = (routeName: RouteNameEnum) => ROUTE_TRANSLATION_KEYS[routeName] ?? routeName;

type NavigationLinkProps = {
  route: RouteType;
  index: number;
  getRoutePath: (route: RouteType) => string;
  onClickMenuItemLocal: (e: any, route: RouteType, index: number) => void;
  children: React.ReactNode;
};

const NavigationLink: React.FC<NavigationLinkProps> = ({
  route,
  index,
  getRoutePath,
  onClickMenuItemLocal,
  children,
}) => {
  const path = getRoutePath(route);
  const isExternalUrl = path?.startsWith('http');

  if (isExternalUrl) {
    return (
      <a
        href={path}
        onClick={(e) => onClickMenuItemLocal(e, route, index)}
        target={route.shouldOpenInNewTab ? '_blank' : undefined}
        rel={route.shouldOpenInNewTab ? 'noopener noreferrer' : undefined}
        data-testid="link"
      >
        {children}
      </a>
    );
  }

  return (
    <Link to={path} onClick={(e) => onClickMenuItemLocal(e, route, index)} data-testid="link">
      {children}
    </Link>
  );
};

type AccordionItemProps = {
  index: number;
};

type AccordionTitleProps = {
  title: string;
  icon?: React.ReactNode;
  onClick?: (index: number) => void;
  route?: RouteType | null;
  opensInANewTab?: boolean;
  isParent?: boolean;
} & AccordionItemProps;

type AccordionContentProps = {
  index: number;
  routes: RouteType[];
};

type LeftNavigationProps = {
  onClickMenuItem?: (route: RouteType) => void;
};
const LeftNavigation: React.FC<LeftNavigationProps> = ({ onClickMenuItem }) => {
  const { t } = useTranslation();
  const { getActiveRoutes, getActiveRoute, getRoutePath, gotoRoute } = useRoutes();
  const [activeParentIndex, setActiveParentIndex] = useState(1);
  const [activeIndex, setActiveIndex] = useState(1);
  const [activeRoute, setActiveRoute] = useState<RouteType | null>(null);
  // Index of the section that is currently animating open. Set only when a
  // section is actually opened (not on sub-item navigation), so clicking a
  // sub-item leaves its section open without replaying the open animation.
  const [openingIndex, setOpeningIndex] = useState<number | null>(null);
  const { isSubsidyPrepaymentNovaEnabled, isGroupMessageNavEnabled, isMarketingAnalyticsNavEnabled, isYelpNavEnabled } =
    useFlags();
  const pendingFocus = useRef<{ index: number; action: 'open' | 'close' } | null>(null);

  useEffect(() => {
    if (!pendingFocus.current) return;
    const { index, action } = pendingFocus.current;
    pendingFocus.current = null;

    const titleEl = document.querySelector(`[data-acc-index="${index}"]`) as HTMLElement | null;
    if (action === 'open') {
      const content = titleEl?.nextElementSibling;
      const firstLink = content?.querySelector('a, button, [tabindex="0"]') as HTMLElement | null;
      firstLink?.focus();
    } else {
      titleEl?.focus();
    }
  }, [activeIndex]);

  const onAccordionClick = (index: number) => {
    const newIndex = activeIndex === index ? -1 : index;
    setActiveIndex(newIndex);
    setActiveRoute(null);
    setActiveParentIndex(newIndex);
    // Animate only the section being opened (null when collapsing).
    setOpeningIndex(newIndex === -1 ? null : newIndex);
  };

  const onClickMenuItemLocal = (e: any, route: RouteType, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveRoute(route);
    setActiveIndex(index);
    // Navigating to a sub-item must not re-trigger the section open animation.
    setOpeningIndex(null);

    // Add user parameters for Teacher Recruitment routes (same as it was in AppStore)
    if (
      (route.name === RouteNameEnum.TEACHER_RECRUITMENT_FULL_TIME ||
        route.name === RouteNameEnum.TEACHER_RECRUITMENT_SUBSTITUTE) &&
      route.path?.startsWith('http')
    ) {
      const urlWithParams = addUserParamsToUrl(route.path);
      if (route.shouldOpenInNewTab) {
        window.open(urlWithParams, '_blank');
      } else {
        window.location.assign(urlWithParams);
      }
    } else {
      gotoRoute(route);
    }

    if (onClickMenuItem) onClickMenuItem(route);
  };

  useEffect(() => {
    const handleNavigationBlocked = () => {
      setActiveRoute(null);
      setActiveIndex(activeParentIndex);
    };

    window.addEventListener('navigationBlocked', handleNavigationBlocked);

    return () => {
      window.removeEventListener('navigationBlocked', handleNavigationBlocked);
    };
  }, [activeParentIndex]);

  // Clear the opening flag once the open animation has finished, so later
  // re-renders don't replay it.
  useEffect(() => {
    if (openingIndex === null) return;
    const timeoutId = setTimeout(() => setOpeningIndex(null), 350);
    return () => clearTimeout(timeoutId);
  }, [openingIndex]);

  return (
    <>
      <Accordion fluid styled className="left-navigation md:mb-10" data-testid="left-navigation">
        <HomeAccordionItem index={1} />
        <div className="nav-divider" aria-hidden />
        <InterestedFamiliesAccordionItem index={2} />
        <MarketingAccordionItem index={12} />
        <SchoolManagementAccordionItem index={3} />
        <BillingAccordionItem index={5} />
        <FinanceAccordionItem index={4} />
        <SchoolWebsiteAccordionItem index={9} />
        <TuitionManagementAccordionItem index={19} />
        <TeacherRecruitmentAccordionItem index={10} />
        <GovernmentSolutionsAccordionItem index={21} />
        <DocumentsAccordionItem index={7} />
        <ReportsAccordionItem index={8} />
        <SchoolSettingsAccordionItem index={11} />
        <div className="nav-divider" aria-hidden />
        <NmLicensingAccordionItem index={14} />
        <HelpCenterAccordionItem index={16} />
        <AdminAccordionItem index={17} />
        <SetupAssistantAccordionItem index={18} />
        <AppsAccordionItem index={20} />
      </Accordion>
    </>
  );

  function HomeAccordionItem({ index }: AccordionItemProps) {
    const route = getActiveRoute(RouteNameEnum.HOME);

    return route ? <AccordionTitle index={index} title="Home" icon={<HomeIcon />} route={route} /> : null;
  }

  function InterestedFamiliesAccordionItem({ index }: AccordionItemProps) {
    const accordionRoutes = [
      RouteNameEnum.INTERESTED_LIST,
      RouteNameEnum.SHARED_LEADS,
      RouteNameEnum.INTERESTED_MESSAGES,
      RouteNameEnum.TOURS,
    ];

    accordionRoutes.push(RouteNameEnum.AVAILABILITY_SURVEY);

    if (isGroupMessageNavEnabled) {
      accordionRoutes.push(RouteNameEnum.GROUP_MESSAGE);
    }
    const routes = getActiveRoutes(accordionRoutes);
    const organization = useOrganization();
    const { idecNetworkId, idecNetworkUrl } = getConfig().wsConfig;
    const isIdecNetwork = organization?.networks?.includes(idecNetworkId);

    if (!routes.length) return null;

    // For IDEC network users, override marketplace URLs with IDEC domain URLs
    const displayRoutes = isIdecNetwork
      ? routes.map((route) => ({
          ...route,
          path: buildMarketplaceLink({ organization, path: route.path, baseUrl: idecNetworkUrl }),
          featureFlag: undefined, // Bypass marketplace URL building in getRoutePath
        }))
      : routes;

    return (
      <>
        <AccordionTitle index={index} title="Interested Families" icon={<UserGroup3Icon />} isParent />
        <AccordionContent index={index} routes={displayRoutes} />
      </>
    );
  }

  function SchoolManagementAccordionItem({ index }: AccordionItemProps) {
    const accordionRoutes = [
      RouteNameEnum.STUDENTS,
      RouteNameEnum.CHECK_IN_CHECK_OUT,
      RouteNameEnum.MEAL_LOGGING,
      RouteNameEnum.UNENROLLMENTS,
      RouteNameEnum.STAFF,
      RouteNameEnum.ROOMS,
      RouteNameEnum.CONTACTS,
      RouteNameEnum.CREDENTIALS,
      RouteNameEnum.PAYROLL,
    ];
    accordionRoutes.push(RouteNameEnum.WONDERCHOOL_APP);
    accordionRoutes.push(RouteNameEnum.BUSINESS_COACHING_SURVEY);
    const routes = getActiveRoutes(accordionRoutes);
    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="School Management" icon={<ManagementIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }
  function BillingAccordionItem({ index }: AccordionItemProps) {
    const accordionRoutes: RouteNameEnum[] = [];
    if (isSubsidyPrepaymentNovaEnabled) {
      accordionRoutes.push(RouteNameEnum.SUBSIDY_PREPAYMENT_NOVA);
    }
    accordionRoutes.push(
      RouteNameEnum.BILLING_SIGN_UP,
      RouteNameEnum.RECURRING_PLANS,
      RouteNameEnum.INVOICES,
      RouteNameEnum.TRANSACTIONS,
      RouteNameEnum.TIME_SPACE_CALCULATOR,
      RouteNameEnum.TAX_FORMS,
      RouteNameEnum.BILLING_SETTINGS
    );

    const routes = getActiveRoutes(accordionRoutes);
    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="Billing" icon={<PaymentsIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }

  function FinanceAccordionItem({ index }: AccordionItemProps) {
    const routes = getActiveRoutes([
      RouteNameEnum.DEBIT_CARD,
      RouteNameEnum.EARN_INTEREST,
      RouteNameEnum.TAXES,
      RouteNameEnum.INSURANCE,
    ]);
    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="Finance" icon={<PaymentsIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }

  function DocumentsAccordionItem({ index }: AccordionItemProps) {
    const route = getActiveRoute(RouteNameEnum.DOCUMENTS);

    return route ? <AccordionTitle index={index} title="Documents" icon={<DocumentIcon />} route={route} /> : null;
  }
  function ReportsAccordionItem({ index }: AccordionItemProps) {
    const routes = getActiveRoutes([
      RouteNameEnum.STAFF_TIMESHEET,
      RouteNameEnum.ATTENDANCE,
      RouteNameEnum.AGING_REPORTS,
      RouteNameEnum.FINANCIAL_SUMMARY,
    ]);
    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="Reports" icon={<ReportsIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }
  function SchoolWebsiteAccordionItem({ index }: AccordionItemProps) {
    const accordionRoutes: RouteNameEnum[] = [];
    if (isMarketingAnalyticsNavEnabled) {
      accordionRoutes.push(RouteNameEnum.MARKETING_ANALYTICS);
    }
    accordionRoutes.push(RouteNameEnum.UPDATE_WEBSITE, RouteNameEnum.VIEW_WEBSITE, RouteNameEnum.WHITE_LABEL_WEBSITE);

    accordionRoutes.push(RouteNameEnum.SETTINGS);

    const routes = getActiveRoutes(accordionRoutes);
    const organization = useOrganization();
    const { idecNetworkId, idecNetworkUrl } = getConfig().wsConfig;
    const isIdecNetwork = organization?.networks?.includes(idecNetworkId);

    if (!routes.length) return null;

    // For IDEC network users, override marketplace URLs with IDEC domain
    const idecOverrideRoutes = [RouteNameEnum.VIEW_WEBSITE, RouteNameEnum.UPDATE_WEBSITE];
    const displayRoutes = isIdecNetwork
      ? routes.map((route) =>
          idecOverrideRoutes.includes(route.name)
            ? {
                ...route,
                path: buildMarketplaceLink({ organization, path: route.path, baseUrl: idecNetworkUrl }),
                featureFlag: undefined, // Bypass marketplace URL building in getRoutePath
              }
            : route
        )
      : routes;

    return (
      <>
        <AccordionTitle index={index} title="Listings & Websites" icon={<WebsiteIcon />} isParent />
        <AccordionContent index={index} routes={displayRoutes} />
      </>
    );
  }

  function SchoolSettingsAccordionItem({ index }: AccordionItemProps) {
    const routes = getActiveRoutes([RouteNameEnum.LOCATIONS, RouteNameEnum.SHARED_DEVICES, RouteNameEnum.PLAN_DETAILS]);
    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="School Settings" icon={<SchoolIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }

  function TuitionManagementAccordionItem({ index }: AccordionItemProps) {
    const routes = getActiveRoutes([
      RouteNameEnum.TUITION_SETTINGS,
      RouteNameEnum.TUITION_GENERATOR,
      RouteNameEnum.COMPARABLE_PROGRAMS,
      RouteNameEnum.TUITION_PRICING_COACHING,
      RouteNameEnum.ANNUAL_TUITION_INCREASES,
    ]);
    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="Tuition Management" icon={<PaymentsIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }

  function MarketingAccordionItem({ index }: AccordionItemProps) {
    const accordionRoutes = [
      RouteNameEnum.OPEN_HOUSE_EVENTS,
      RouteNameEnum.SEARCH_SOCIAL_MARKETING,
      RouteNameEnum.WONDERSCHOOL_PHONE_NUMBER,
      RouteNameEnum.FLYERS,
      RouteNameEnum.AI_PHONE,
      RouteNameEnum.GOOGLE_MY_BUSINESS,
    ];
    if (isYelpNavEnabled) {
      accordionRoutes.push(RouteNameEnum.YELP);
    }
    const routes = getActiveRoutes(accordionRoutes);
    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="Marketing" icon={<BroadcastIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }

  function HelpCenterAccordionItem({ index }: AccordionItemProps) {
    const route = getActiveRoute(RouteNameEnum.HELP_CENTER);

    return route ? (
      <AccordionTitle index={index} title="Help Center" icon={<HelpIcon />} route={route} opensInANewTab={true} />
    ) : null;
  }

  function TeacherRecruitmentAccordionItem({ index }: AccordionItemProps) {
    const routes = getActiveRoutes([
      RouteNameEnum.TEACHER_RECRUITMENT_FULL_TIME,
      RouteNameEnum.TEACHER_RECRUITMENT_SUBSTITUTE,
    ]);

    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="Teacher Recruitment" icon={<UserIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }

  function GovernmentSolutionsAccordionItem({ index }: AccordionItemProps) {
    const routes = getActiveRoutes([RouteNameEnum.TRI_SHARE]);
    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="Government Solutions" icon={<FoodProgramIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }

  function NmLicensingAccordionItem({ index }: AccordionItemProps) {
    const route = getActiveRoute(RouteNameEnum.NM_LICENSING);
    const organization = useOrganization();
    const nmNetworkId = getConfig().wsConfig.nmNetworkId;
    const flags = useFlags();
    // LaunchDarkly converts kebab-case flag names to camelCase in the React SDK
    const isNmLicensingEnabled = flags.nmLicensingEnabled as boolean;

    // Only show for organizations affiliated with the NM network AND when feature flag is enabled
    if (!route || !organization?.networks?.includes(nmNetworkId) || !isNmLicensingEnabled) return null;

    return <AccordionTitle index={index} title="Licensing" icon={<SuccessIcon />} route={route} />;
  }

  function AdminAccordionItem({ index }: AccordionItemProps) {
    const routes = getActiveRoutes([
      RouteNameEnum.PROVIDERS,
      RouteNameEnum.USERS,
      RouteNameEnum.NETWORKS,
      RouteNameEnum.AREA_51,
      RouteNameEnum.ADMIN_DOCUMENTS,
      RouteNameEnum.ENROLLMENTS_ADMIN,
      RouteNameEnum.MIGRATION_REPORT,
      RouteNameEnum.SYSTEM_NOTIFICATIONS,
    ]);
    if (!routes.length) return null;

    return (
      <>
        <AccordionTitle index={index} title="Admin" icon={<SettingsIcon />} isParent />
        <AccordionContent index={index} routes={routes} />
      </>
    );
  }

  function AppsAccordionItem({ index }: AccordionItemProps) {
    const route = getActiveRoute(RouteNameEnum.APPS);
    if (!route) return null;

    return (
      <AccordionTitle index={index} title={t('apps.title')} icon={<AppStoreIcon className="size-6" />} route={route} />
    );
  }

  function SetupAssistantAccordionItem({ index }: AccordionItemProps) {
    const route = getActiveRoute(RouteNameEnum.SETUP_ASSISTANT);

    return route ? <AccordionTitle index={index} title="Setup Assistant" icon={<SuccessIcon />} route={route} /> : null;
  }

  // Wrapper for Accordion.Title
  function AccordionTitle({ index, isParent, route, ...rest }: AccordionTitleProps) {
    const isActive = activeIndex === index;

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        pendingFocus.current = { index, action: isActive ? 'close' : 'open' };
        onAccordionClick(index);
      }
    };

    return (
      <Accordion.Title
        active={isActive}
        index={index}
        onClick={() => onAccordionClick(index)}
        data-testid="title"
        data-acc-index={index}
        {...(isParent && { tabIndex: 0, role: 'button', onKeyDown: handleKeyDown, 'aria-expanded': isActive })}
      >
        {isParent && <AccordionTitleDiv index={index} isParent={isParent} {...rest} />}
        {!isParent && route && (
          <NavigationLink
            route={route}
            index={index}
            getRoutePath={getRoutePath}
            onClickMenuItemLocal={onClickMenuItemLocal}
          >
            <AccordionTitleDiv index={index} isParent={isParent} {...rest} />
          </NavigationLink>
        )}
      </Accordion.Title>
    );
  }
  function AccordionTitleDiv({ title, icon, index, isParent, opensInANewTab }: AccordionTitleProps) {
    const isActive = activeIndex === index;
    return (
      <div className={styles.accordionTitle} data-testid="acc-title">
        <div className={styles.icon} data-testid="acc-icon">
          {icon}
        </div>
        <div className={styles.title} data-testid={`${title.toLowerCase()}-link`}>
          {t(title)}
        </div>
        {/* Right-aligned (like the chevron + sub-item link-outs) for consistency */}
        {opensInANewTab && <LinkOutIcon className="size-4 shrink-0" />}
        {!!isParent && (
          <CaretDownIcon
            data-testid="acc-icon"
            className={`size-4 shrink-0 transition-transform ${isActive ? 'rotate-180' : ''}`}
          />
        )}
      </div>
    );
  }
  function AccordionContent({ index, routes }: AccordionContentProps) {
    return (
      <Accordion.Content
        active={activeIndex === index}
        className={openingIndex === index ? 'nav-section-opening' : undefined}
        data-testid="acc-content"
      >
        <Menu vertical className="rounded-lg">
          {routes.map((route) => {
            const path = getRoutePath(route);
            const isExternalUrl = path?.startsWith('http');

            if (isExternalUrl) {
              return (
                <Menu.Item
                  key={route.name}
                  as="a"
                  href={path}
                  active={activeRoute === route}
                  onClick={(e) => onClickMenuItemLocal(e, route, index)}
                  target={route.shouldOpenInNewTab ? '_blank' : undefined}
                  rel={route.shouldOpenInNewTab ? 'noopener noreferrer' : undefined}
                  data-testid={`${route.name.toLowerCase()}-link`}
                >
                  <div className="flex w-full items-center justify-between">
                    <span>{t(getRouteTranslationKey(route.name))}</span>
                    <div className="flex items-center gap-1">
                      {route.isNew && (
                        <span className="ml-2 rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700">
                          NEW
                        </span>
                      )}
                      {route.shouldOpenInNewTab ? (
                        <span>
                          <LinkOutIcon className="ml-2 size-4" />
                        </span>
                      ) : null}
                    </div>
                  </div>
                </Menu.Item>
              );
            }

            return (
              <Menu.Item
                key={route.name}
                as={Link}
                to={{ pathname: path }}
                active={activeRoute === route}
                onClick={(e) => onClickMenuItemLocal(e, route, index)}
                data-testid={`${route.name.toLowerCase()}-link`}
              >
                <div className="flex w-full items-center justify-between">
                  <span>{t(getRouteTranslationKey(route.name))}</span>
                  <div className="flex items-center gap-1">
                    {route.isNew && (
                      <span className="ml-2 rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-semibold text-blue-700">
                        NEW
                      </span>
                    )}
                    {route.shouldOpenInNewTab ? (
                      <span>
                        <LinkOutIcon className="ml-2 size-4" />
                      </span>
                    ) : null}
                  </div>
                </div>
              </Menu.Item>
            );
          })}
        </Menu>
      </Accordion.Content>
    );
  }
};

export default LeftNavigation;
